"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, FileCode, MapPin, Star, Database, ChartBar as BarChart3, ArrowRight, Check as CheckCircle2, Calendar } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const processSteps = [{
  number: "01",
  title: "Deep Technical & Strategic SEO Audit",
  icon: Search,
  description: "I personally analyze your website's technical health, crawlability, indexation, and competitor footprints to uncover immediate growth levers.",
  deliverable: "Custom Audit & Prioritized Action Roadmap"
}, {
  number: "02",
  title: "On-Page & High-Intent Optimization",
  icon: FileCode,
  description: "I restructure page architecture, refine metadata, and align target content directly with commercial buyer intent that converts visitors into leads.",
  deliverable: "Optimized Core Pages & Content Strategy"
}, {
  number: "03",
  title: "Local Citations & Domain Authority",
  icon: MapPin,
  description: "I establish structured local citations and acquire high-quality contextual backlinks to build sustainable search authority in targeted geographic regions.",
  deliverable: "Verified Citation Profile & Authority Growth"
}, {
  number: "04",
  title: "Review Growth & Reputation Building",
  icon: Star,
  description: "I design strategies to systematically earn authentic customer reviews, strengthening trust signals for Google Map Pack rankings and user confidence.",
  deliverable: "Reputation Strategy & Conversion Boost"
}, {
  number: "05",
  title: "Structured Data & Schema Deployment",
  icon: Database,
  description: "I author advanced JSON-LD schema markup so search engines and AI assistants clearly interpret your brand, services, and local entity signals.",
  deliverable: "Full Schema Deployment & AI Readiness"
}, {
  number: "06",
  title: "Transparent Reporting & ROI Review",
  icon: BarChart3,
  description: "Every month, you receive a clear, video-backed performance review tracking non-branded organic traffic, key rankings, and genuine business leads.",
  deliverable: "Monthly Performance Review & Strategic Refinement"
}];
export default function Process() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".process-card-item", {
        y: 35
      });
      gsap.to(".process-card-item", {
        opacity: 1,
        y: 0,
        duration: 0.65,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".process-card-item", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="strategicseoprocess" data-nav="light" className="bg-background text-foreground relative py-20 md:py-28 overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs uppercase tracking-wider font-semibold border border-accent/30 bg-secondary text-secondary-foreground">
            Proven SEO Framework
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            My Step-by-Step Strategic SEO Process
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            No bloated agency fluff or generic templates. When you partner with me, you get a direct, hands-on methodology tailored to elevate your business search rankings and revenue.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-12 left-[8%] right-[8%] h-0.5 bg-gradient-to-r from-accent/20 via-accent/60 to-accent/20 -z-10" aria-hidden="true" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {processSteps.map((step, i) => {
            const IconComponent = step.icon;
            return <div key={step.number} data-index={i} className="process-card-item h-full" style={{
              opacity: 0
            }}>
                  <Card className="h-full border border-border/80 bg-card hover:border-primary/40 hover:shadow-lg transition-all duration-300 relative flex flex-col justify-between overflow-hidden group">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-80 group-hover:opacity-100 transition-opacity" />

                    <div>
                      <CardHeader className="pt-8 pb-4 relative">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center shadow-md ring-4 ring-secondary/60">
                            {step.number}
                          </div>

                          <div className="p-2.5 rounded-xl bg-secondary text-primary border border-accent/20">
                            <IconComponent className="size-6 text-primary" />
                          </div>
                        </div>

                        <CardTitle className="text-xl font-bold font-serif text-card-foreground group-hover:text-primary transition-colors">
                          {step.title}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="pb-6">
                        <CardDescription className="text-sm text-muted-foreground leading-relaxed mb-6">
                          {step.description}
                        </CardDescription>

                        <div className="p-3 rounded-lg bg-muted/60 border border-border/60 flex items-start gap-2.5">
                          <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                          <div className="text-xs font-medium text-foreground">
                            <span className="text-muted-foreground font-normal block text-[13px] uppercase tracking-wider">Key Deliverable</span>
                            {step.deliverable}
                          </div>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-16 md:mt-20 p-8 md:p-12 rounded-2xl bg-card border border-border shadow-md text-center max-w-4xl mx-auto relative overflow-hidden">
          <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-secondary/60 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -left-12 -top-12 w-48 h-48 bg-accent/10 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground font-serif">
              Ready for SEO Strategy Built Directly for Your Business?
            </h3>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
              Work directly with Yassine Essaadi. No account managers, no middle-tier staff, just tailored expertise focused on your sustainable search growth.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 shadow-md w-full sm:w-auto">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary text-foreground font-medium px-6 w-full sm:w-auto">
                <Link to="/CaseStudiesPage" className="inline-flex items-center gap-2">
                  <span>Explore My Case Studies</span>
                  <ArrowRight className="size-4 text-primary" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}